import { money } from "../../i18n/intl";
import type { Role, UserListEntry } from "../../api/types";

export const ROLE_LABELS: Record<string, string> = {
  OWNER: "Owner",
  ADMIN: "Admin",
  MANAGER: "Manager",
  FLORIST: "Florist",
  COURIER: "Courier",
  HOUSEKEEPING: "Housekeeping",
};

export const VEHICLE_LABELS: Record<string, string> = {
  BIKE: "Bike",
  MOTORBIKE: "Motorbike",
  CAR: "Car",
  VAN: "Van",
};

export function roleLabel(role: Role | string): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * A courier's vehicle and plate — "Bike · 1234ABC" — or null for everyone
 * else. The table has always shown this in place of the job title for a
 * courier; the sheet says the same thing, from here, so the two cannot drift.
 */
export function courierLine(u: UserListEntry): string | null {
  if (!u.courierProfile) return null;
  const parts = [
    VEHICLE_LABELS[u.courierProfile.vehicleType] ?? u.courierProfile.vehicleType,
    u.courierProfile.vehiclePlate,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * G-102 — the job title WORTH SHOWING next to the role, or null.
 *
 * Owner, 2026-09-22: *"no need to have role and job title. it creates double
 * text"*. In the real data the two columns usually say the same word — role
 * "Florist" beside job title "Florist" — but not always: the Admin row carries
 * a written-out description. So the phone layout shows the role, and adds the
 * job title only when it is not merely the role again. A courier's vehicle
 * line always counts as different, because it is not a title at all.
 *
 * Compared case- and space-insensitively: "florist " and "Florist" are the
 * same word typed twice, and showing both would be the bug this removes.
 */
export function distinctJobTitle(u: UserListEntry): string | null {
  const courier = courierLine(u);
  if (courier) return courier;
  const title = u.jobTitle?.trim();
  if (!title) return null;
  return title.toLowerCase() === roleLabel(u.role).toLowerCase() ? null : title;
}

/**
 * G-106 — the one line under the name on a card: what this person DOES.
 *
 * Owner ruling 2026-09-23, made with G-102's opposite call in front of them:
 * the card says the job title, and the role only when there is no job title.
 * In practice the two usually read the same, because the server DEFAULTS a new
 * person's job title to their role name (`defaultJobTitle`) — so this is not
 * "the role disappeared", it is "the person's own words win where they wrote
 * any". The Admin's "Being myself" shows instead of "Admin"; a florist who
 * never touched the field still reads "Florist".
 *
 * The role has not gone anywhere: `TeamMemberSheet` states it as a badge,
 * one tap away.
 *
 * A COURIER IS NOT A SPECIAL CASE HERE, and that is a correction: the first
 * cut returned the vehicle line first, the way the old desktop table's Job
 * title column did. /code-review caught what that costs on a card — a courier
 * read "Bike · 1234ABC" and nothing on the card said what they were. A
 * registration plate is neither a job title nor a role. The tile adds the
 * vehicle after the title (`tileRoleLine`), and the sheet shows it in full.
 */
export function cardTitleLine(u: UserListEntry): string {
  // `??` would not do: the contact PATCH stores "" for a cleared job title,
  // and an empty string is a value, not an absence.
  const title = u.jobTitle?.trim();
  return title ? title : roleLabel(u.role);
}

/**
 * The name on a card, where the table's two columns become one line.
 *
 * `fullName` is NOT a third field: the server composes it from these same two
 * (`composeFullName`, users.service.ts:113), so joining `firstName ?? fullName`
 * with `lastName` prints the surname twice for anyone who has a last name and
 * no first one — "Smith Smith". The structured pair is used only when there IS
 * a first name; otherwise `fullName` already says everything known.
 */
export function displayName(u: UserListEntry): string {
  if (u.firstName) {
    return [u.firstName, u.lastName].filter(Boolean).join(" ");
  }
  return u.fullName;
}

export function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * G-136 — the badge for someone who cannot sign in yet, or null for an
 * account in use. "Invited" means the mail server ACCEPTED the invitation;
 * nobody can know it reached the inbox, so the label never says "delivered".
 */
export function accountBadge(
  u: Pick<UserListEntry, "accountStatus">,
): { label: string; className: string } | null {
  switch (u.accountStatus) {
    case "INVITE_SENDING":
      return { label: "Invite sending", className: "badge muted" };
    case "INVITED":
      return { label: "Invited", className: "badge primary" };
    case "INVITE_FAILED":
      return { label: "Invite not sent", className: "badge danger" };
    case "INVITE_EXPIRED":
      return { label: "Invite expired", className: "badge warning" };
    case "SETUP_PENDING":
      return { label: "Not set up", className: "badge warning" };
    default:
      return null;
  }
}

/** G-145 — "1st of the month", "22nd of the month" (a day the month lacks is paid on its last day). */
export function payDayLabel(day: number): string {
  const tens = day % 100;
  const suffix =
    tens >= 11 && tens <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] ?? "th";
  return `${day}${suffix} of the month`;
}

/** G-145 — a date without the time: "2 Sep 2026". */
export function shortDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** G-145 — up to two letters for a tile's avatar: "Laura Soler" → "LS". */
export function initials(u: Pick<UserListEntry, "firstName" | "lastName" | "fullName">): string {
  const words =
    u.firstName || u.lastName
      ? [u.firstName, u.lastName]
      : (u.fullName ?? "").split(/\s+/);
  const letters = words
    .map((w) => (w ?? "").trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("");
  return letters.toUpperCase() || "?";
}


/**
 * G-145 — the pay line in a tile's footer, or null. `showPay` is the caller's
 * permission (`canEditPay`: an owner looking at someone who is not an owner),
 * stated rather than inferred from which fields the payload happens to carry.
 * A person is salaried OR hourly, never both (`assertSalariedOrHourly`), so
 * the salary wins only because it is the one that is set.
 */
export function tilePayLine(
  u: Pick<UserListEntry, "role" | "hourlyRateEur" | "monthlySalaryEur">,
  showPay: boolean,
): string | null {
  if (!showPay) return null;
  if (u.role === "COURIER") return "Per delivery";
  // Money through the app's one formatter (`money`), which shows "—" for nothing.
  if (u.monthlySalaryEur != null) return `${money(u.monthlySalaryEur)} / month`;
  if (u.hourlyRateEur != null) return `${money(u.hourlyRateEur)} / hour`;
  return "No rate yet";
}

/** G-145 — the right side of a tile's footer: PIN, contract, language. */
export function tileMetaLine(
  u: Pick<UserListEntry, "role" | "shopPinConfigured" | "workContractConfigured">,
  languageLabel?: string | null,
): string {
  const parts: string[] = [];
  // Couriers have no shop-floor PIN at all (`canSetPin`), so "No PIN" would be noise.
  if (u.role !== "COURIER") parts.push(u.shopPinConfigured ? "PIN set" : "No PIN");
  if (u.workContractConfigured) parts.push("Contract");
  if (languageLabel) parts.push(languageLabel);
  return parts.join(" · ");
}

/** G-145 — the role line under a tile's name: "Florist · Clot", "Courier · Bike". */
export function tileRoleLine(u: UserListEntry): string {
  const shop = u.locationAssignments[0]?.location.name;
  const vehicle = u.courierProfile
    ? (VEHICLE_LABELS[u.courierProfile.vehicleType] ?? u.courierProfile.vehicleType)
    : null;
  return [cardTitleLine(u), shop ?? vehicle].filter(Boolean).join(" · ");
}

/** G-145 — "Email not confirmed" for an account in use whose address never confirmed. */
export function unconfirmedEmail(
  u: Pick<UserListEntry, "accountStatus" | "emailVerified">,
): boolean {
  return (u.accountStatus ?? "ACTIVE") === "ACTIVE" && u.emailVerified === false;
}

/** G-145 — the Team header's count line: "8 people · 1 invitation open". */
export function teamCountLine(users: Pick<UserListEntry, "isActive" | "accountStatus">[]): string {
  const active = users.filter((u) => u.isActive);
  const open = active.filter(
    (u) => u.accountStatus === "INVITED" || u.accountStatus === "INVITE_SENDING",
  ).length;
  const people = `${active.length} ${active.length === 1 ? "person" : "people"}`;
  return open > 0 ? `${people} · ${open} invitation${open === 1 ? "" : "s"} open` : people;
}

/** G-136 — the one sentence that explains an invitation's state, for the Invitation dialog. */
export function inviteSentence(u: Pick<UserListEntry, "accountStatus" | "inviteExpiresAt">): string {
  const until = u.inviteExpiresAt ? formatDate(u.inviteExpiresAt) : null;
  switch (u.accountStatus) {
    case "INVITE_SENDING":
      return "The invitation is waiting to be sent.";
    case "INVITED":
      return until
        ? `The invitation was handed to the mail server. Its link works until ${until}.`
        : "The invitation was handed to the mail server.";
    case "INVITE_FAILED":
      return "The invitation could not be sent. Check the address, then send a new one.";
    case "INVITE_EXPIRED":
      return "The invitation's link ran out before it was used. Send a new one.";
    case "SETUP_PENDING":
      return "There is no invitation waiting. Send one so they can choose their password.";
    default:
      return "This person has set up their account.";
  }
}

